import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ChatService, ChatMessage } from '../../services/chat/chat.service';
import { MarkdownService } from '../../services/markdown/markdown.service';
import { ConfirmationModalComponent } from '../shared/confirmation-modal/confirmation-modal.component';
import { Subscription, interval } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { LogTackingService } from '../../services/logs/log-tacking.service';
import { LogsTrackingRequest, LogsTrackingResponse } from '../../interfaces/logs_tracking.interface.';
import { getUserRoles, hasRole } from '../../utils/roles.util';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-chat-boris',
  standalone: true,
  imports: [CommonModule, FormsModule, ConfirmationModalComponent],
  templateUrl: './chat-boris.component.html',
  styleUrls: ['./chat-boris.component.css']
})
export class ChatBorisComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('scrollMe') private readonly myScrollContainer!: ElementRef;

  messages: ChatMessage[] = [];
  newMessage: string = '';
  isLoading: boolean = false;
  showClearChatModal: boolean = false;
  isFullScreen: boolean = false;
  isOnline: boolean = true;
  userRoles: string[] = [];
  chatMode: 'documentos' | 'cdt' = 'documentos';
  private messageSub!: Subscription;
  private connectionSub!: Subscription;
  private healthCheckSub!: Subscription;
  private readonly INACTIVITY_TIMEOUT_MS = (environment.chat?.inactivityTimeoutMinutes ?? 10) * 60 * 1000;
  private inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  log_info: LogsTrackingRequest = {
      email: localStorage.getItem('userName') ?? '',
      action: 'Chat Documentos Vinculacion',
      details: '',
      ip_address: localStorage.getItem('ip') ?? ''
    }

  get isAdmin(): boolean {
    return hasRole(this.userRoles, 'admin');
  }

  get isCdt(): boolean {
    return hasRole(this.userRoles, 'cdt') || (this.isAdmin && this.chatMode === 'cdt');
  }

  selectChatMode(mode: 'documentos' | 'cdt'): void {
    if (this.chatMode === mode) return;
    this.chatMode = mode;
    this.chatService.clearChat();
    this.messages = [{
      id: 'initial_greeting',
      content: this.initialGreeting,
      sender: 'bot',
      timestamp: new Date()
    }];
    // Switching mode is activity and starts a fresh session.
    this.resetInactivityTimer();
  }

  get initialGreeting(): string {
    return this.isCdt
      ? '¡Hola! Soy Boris. Pregúntame sobre saldos, metas, crecimiento o tasas de CDT y Cuenta de Ahorro.'
      : '¡Hola! Soy Boris. ¿En qué puedo ayudarte hoy con los documentos de vinculacion de nuestros clientes?';
  }

  get inputPlaceholder(): string {
    if (!this.isOnline) return 'Chat desconectado. Esperando conexión...';
    return this.isCdt
      ? 'Escribe tu mensaje aquí... (Ej: ¿Como va el cumplimiento CDT en el mes de Marzo?'
      : 'Escribe tu mensaje aquí... (Ej: Busca documentos del NIT 900123456)';
  }

  get footerText(): string {
    return this.isCdt
      ? 'Boris es una IA diseñada para consultar KPIs IRIS de CDT y Cuenta de Ahorro.'
      : 'Boris es una IA diseñada para consultar el estado de los documentos de vinculación.';
  }

  get logAction(): string {
    return this.isCdt ? 'Chat KPIs CDT Ahorro' : 'Chat Documentos Vinculacion';
  }

  private readonly formattedCache = new Map<string, SafeHtml>();
  private autoScrollEnabled = true;
  private lastRenderKey = '';
  private readonly nearBottomThreshold = 80;
  showScrollToBottom = false;
  unseenMessages = 0;

  constructor(
    private readonly chatService: ChatService,
    private readonly _logsService: LogTackingService,
    private readonly markdown: MarkdownService,
    private readonly sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    // On entry: clear messages + refresh NORMAL chat sessionId (localStorage).
    // This should not impact CDT chat session handling.
    this.chatService.resetNormalChatForComponentEntry();

    // Also start a fresh CDT session so the UI owns the memory Key from the
    // very first message (cdt-role users never go through selectChatMode).
    this.chatService.startNewSessionCdt();

    try {
      this.userRoles = getUserRoles();
    } catch {
      this.userRoles = [];
    }

    this.log_info.action = this.logAction;

    this.messageSub = this.chatService.messages$.subscribe(msgs => {
      this.messages = msgs;
      this.isLoading = msgs.some(m => m.isLoading);
    });

    this.connectionSub = this.chatService.connectionStatus$.subscribe(status => {
      this.isOnline = status;
    });

    this.checkHealthStatus();

    this.healthCheckSub = interval(30000)
      .pipe(
        switchMap(() => this.isCdt ? this.chatService.checkHealthCdt() : this.chatService.checkHealth())
      )
      .subscribe({
        next: () => { },
        error: (err) => {
          console.warn('Health check failed:', err);
        }
      });

    this.scrollToBottom();

    if (this.messages.length === 0) {
      this.messages.push({
        id: 'initial_greeting',
        content: this.initialGreeting,
        sender: 'bot',
        timestamp: new Date()
      });
    }

    // Start the inactivity watchdog so stale sessions get refreshed automatically.
    this.resetInactivityTimer();
  }

  ngAfterViewChecked() {
    const last = this.messages[this.messages.length - 1];
    const key = last
      ? `${this.messages.length}::${last.id}::${last.content?.length ?? 0}::${last.isLoading}`
      : '';
    if (key === this.lastRenderKey) return;
    this.lastRenderKey = key;

    if (this.autoScrollEnabled) {
      this.scrollToBottom();
    } else if (last && last.sender === 'bot' && !last.isLoading) {
      this.unseenMessages++;
      this.showScrollToBottom = true;
    }
  }

  ngOnDestroy() {
    if (this.messageSub) {
      this.messageSub.unsubscribe();
    }
    if (this.connectionSub) {
      this.connectionSub.unsubscribe();
    }
    if (this.healthCheckSub) {
      this.healthCheckSub.unsubscribe();
    }
    this.clearInactivityTimer();
  }

  /**
   * Restarts the inactivity countdown. Called on any meaningful user activity
   * (sending messages, typing, switching mode). When it elapses, the chat
   * session is regenerated so the agent does not get confused by stale context.
   */
  private resetInactivityTimer(): void {
    this.clearInactivityTimer();
    this.inactivityTimer = setTimeout(() => {
      this.handleInactivityTimeout();
    }, this.INACTIVITY_TIMEOUT_MS);
  }

  private clearInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  /**
   * On prolonged inactivity, start a fresh session and reset the visible
   * conversation so the next interaction begins with a clean context.
   */
  private handleInactivityTimeout(): void {
    this.chatService.refreshAllSessions();
    this.messages = [{
      id: 'initial_greeting',
      content: this.initialGreeting,
      sender: 'bot',
      timestamp: new Date()
    }];
    this.log_info.details = 'Info: Sesión de chat Boris regenerada por inactividad';
    this.createTrackingLog();
  }

  sendMessage(): void {
    if (!this.newMessage.trim() || this.isLoading || !this.isOnline) return;

    // Any send counts as activity: keep the session alive.
    this.resetInactivityTimer();

    const msg = this.newMessage;
    this.newMessage = '';
    this.resetTextareaHeight();
    this.autoScrollEnabled = true;
    this.showScrollToBottom = false;
    this.unseenMessages = 0;

    const send$ = this.isCdt
      ? this.chatService.sendMessageCdt(msg)
      : this.chatService.sendMessage(msg);

    send$.subscribe({
      next: () => {
        this.log_info.details = `Success: Usuario envió mensaje al chat Boris: "${msg}"`;
        this.createTrackingLog();
      },
      error: (err) => {
        console.error('Error sending message', err);
        this.log_info.details = `Error: Fallo al enviar mensaje al chat Boris: ${err?.message ?? err}`;
        this.createTrackingLog();
      }
    });
  }

  handleKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  scrollToBottom(smooth = false): void {
    const el = this.myScrollContainer?.nativeElement;
    if (!el) return;
    if (smooth && 'scrollTo' in el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }

  onMessagesScroll(): void {
    const el = this.myScrollContainer?.nativeElement;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nearBottom = distanceFromBottom <= this.nearBottomThreshold;
    this.autoScrollEnabled = nearBottom;
    if (nearBottom) {
      this.showScrollToBottom = false;
      this.unseenMessages = 0;
    } else if (distanceFromBottom > 200) {
      this.showScrollToBottom = true;
    }
  }

  scrollToLatest(): void {
    this.autoScrollEnabled = true;
    this.showScrollToBottom = false;
    this.unseenMessages = 0;
    this.scrollToBottom(true);
  }

  onTextareaInput(event: Event): void {
    // Typing is activity too.
    this.resetInactivityTimer();
    const ta = event.target as HTMLTextAreaElement;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 140)}px`;
  }

  private resetTextareaHeight(): void {
    const ta = document.querySelector<HTMLTextAreaElement>('.chat-input-area textarea');
    if (ta) ta.style.height = 'auto';
  }

  clearChat() {
    this.showClearChatModal = true;
  }

  confirmClearChat() {
    this.chatService.clearChat();
    this.messages = [{
      id: 'initial_greeting',
      content: this.initialGreeting,
      sender: 'bot',
      timestamp: new Date()
    }];
    this.showClearChatModal = false;
    // Emptying the trash starts a fresh session; restart the inactivity window.
    this.resetInactivityTimer();
  }

  cancelClearChat() {
    this.showClearChatModal = false;
  }

  toggleFullScreen() {
    this.isFullScreen = !this.isFullScreen;
  }

  downloadChatBackup(): void {
    const realMessages = this.messages.filter(m => !m.isLoading);
    if (realMessages.length === 0) return;

    const mode = this.isCdt ? 'KPIs CDT / Ahorro' : 'Documentos de Vinculación';
    const user = localStorage.getItem('userName') ?? 'Desconocido';
    const now = new Date();
    const dateStr = now.toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' });
    const timeStr = now.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });

    const separator = '─'.repeat(60);
    const lines: string[] = [
      '╔══════════════════════════════════════════════════════════╗',
      '║              RESPALDO DE CONVERSACIÓN - BORIS            ║',
      '╚══════════════════════════════════════════════════════════╝',
      '',
      `  Fecha de descarga : ${dateStr} a las ${timeStr}`,
      `  Usuario           : ${user}`,
      `  Modo de chat      : ${mode}`,
      `  Total de mensajes : ${realMessages.length}`,
      '',
      separator,
      '',
    ];

    for (const msg of realMessages) {
      const sender = msg.sender === 'user' ? '👤 Usuario' : '🤖 Boris';
      const time = new Date(msg.timestamp).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
      // Strip HTML tags from bot messages (markdown rendered to HTML)
      const plainText = msg.content.replace(/<[^>]*>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
      lines.push(`[${time}]  ${sender}`);
      lines.push(plainText.trim());
      lines.push('');
    }

    lines.push(separator);
    lines.push('Fin del respaldo · Generado por Boris AI · Iris');

    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    const fileName = `boris-chat-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}.txt`;
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  formatMessage(msg: ChatMessage): SafeHtml {
    const key = `${msg.id}::${msg.content}`;
    const cached = this.formattedCache.get(key);
    if (cached) return cached;

    const html = msg.sender === 'bot'
      ? this.markdown.render(msg.content)
      : this.escapeAndBreak(msg.content);
    const safe = this.sanitizer.bypassSecurityTrustHtml(html);
    this.formattedCache.set(key, safe);
    return safe;
  }

  private escapeAndBreak(text: string): string {
    return text
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('\n', '<br/>');
  }

  checkHealthStatus(): void {
    const health$ = this.isCdt ? this.chatService.checkHealthCdt() : this.chatService.checkHealth();
    health$.subscribe({
      next: () => { },
      error: (err) => {
        console.warn('Initial health check failed:', err);
      }
    });
  }

  createTrackingLog() {
      this._logsService.generateTrackingLog(this.log_info).subscribe({
        next: (response: LogsTrackingResponse) => { },
        error: (error) => {
          console.log(error);
        }
      });
    }
}
